import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, DropdownList, Text} from '@forecast-it/design-system';

export type XeroLineDropdownOption = {key: string; label: string; onSelect: () => void};

export type XeroLineDropdownProps = {
	/** The chosen option's label. Undefined shows the placeholder, in the placeholder colour. */
	value: string | undefined;
	placeholder: string;
	/** Every selectable option, "None" included by the caller where the column offers it. */
	options: XeroLineDropdownOption[];
	/** Shown alone, and not selectable, when there are no options: why the list is empty. */
	emptyMessage?: string;
	disabled?: boolean;
	/** Puts the field in its error state with this text below it; the field shows the placeholder. */
	errorText?: string;
	'data-cy'?: string;
};

// Not a list item, so it cannot be picked, but padded like one so it lines up with the field's text.
const EmptyMessage = styled.div`
	padding: ${({theme}) => `${theme.sys.spacing['200']} ${theme.sys.spacing['400']}`};
`;

/*
 * The design system's list is never taller than 490px, however little room the screen has, so a
 * long list opened low on a short screen runs off it. The menu publishes the room it has on the
 * side it opened, and the list keeps to the smaller of the two. Adds no box of its own.
 */
const ListHeightCap = styled.div.attrs({'data-list-height-cap': ''})`
	display: contents;

	& [role='menu'] {
		max-height: min(490px, var(--radix-dropdown-menu-content-available-height, 490px));
	}
`;

/**
 * One line's Item, Account or tracking dropdown in the Export to Xero window.
 *
 * The open list is exactly as wide as the field, and an option longer than that wraps rather than
 * being cut off. A chosen value too long for the field is cut off with "…" and shown in full on hover.
 */
const XeroLineDropdown = ({
	value,
	placeholder,
	options,
	emptyMessage,
	disabled = false,
	errorText,
	'data-cy': dataCy,
}: XeroLineDropdownProps): JSX.Element => {
	const showsPlaceholder = value === undefined || !!errorText;
	const label = showsPlaceholder ? placeholder : value;
	const [open, setOpen] = useState(false);

	/*
	 * An Escape that reaches the browser window closes the whole Export window and discards every
	 * pick in it. While the list is open, Escape is caught before anything else sees it and closes
	 * only the list. It cannot be stopped later: the list closes itself on the way down, and once
	 * closed nothing here is left to stop the key on its way back up.
	 */
	useEffect(() => {
		if (!open) {
			return undefined;
		}
		const closeOnlyTheList = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				setOpen(false);
			}
		};
		window.addEventListener('keydown', closeOnlyTheList, true);
		return () => window.removeEventListener('keydown', closeOnlyTheList, true);
	}, [open]);

	return (
		<ListHeightCap>
			<DropdownList open={open} onOpenChange={setOpen}>
				<DropdownList.Trigger disabled={disabled}>
					<ButtonDropdownField
						size={'small'}
						disabled={disabled}
						isDefaultLabel={showsPlaceholder}
						hasError={!!errorText}
						errorText={errorText}
						data-cy={dataCy}
					>
						<Text ellipsis tooltipOnEllipsis color={showsPlaceholder ? Text.color.auxiliary : Text.color.main}>
							{label}
						</Text>
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content useTriggerWidth align={'start'}>
					{options.length === 0 && emptyMessage ? (
						<EmptyMessage>
							<Text color={Text.color.auxiliary}>{emptyMessage}</Text>
						</EmptyMessage>
					) : (
						options.map(option => (
							<DropdownList.Item key={option.key} onClick={option.onSelect}>
								{option.label}
							</DropdownList.Item>
						))
					)}
				</DropdownList.Content>
			</DropdownList>
		</ListHeightCap>
	);
};

export default XeroLineDropdown;
